import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from "react";
import {
  createFileRoute,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";
import { usePrivy } from "@privy-io/react-auth";
import { toast } from "sonner";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";

dayjs.extend(advancedFormat);
import {
  classifyMatchmakingCancelError,
  useCanReportBatch,
  useMatchmakingCancel,
  useMatchmakingDetails,
  useStats,
} from "@/lib/hooks/useApi";
import { ReportPlayerDialog } from "@/components/ReportPlayerDialog";
import { ReportModeToggle } from "@/components/ReportModeToggle";
import { useMatchmakingSse } from "@/lib/hooks/useMatchmakingSse";
import {
  useActiveSeeds,
  useCancelRefundTxs,
  useSearchStartEpoch,
} from "@/lib/hooks/useActiveMatchmaking";
import { useFindMatch } from "@/lib/hooks/useFindMatch";
import { useBalance } from "@/lib/hooks/useBalance";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChevronDown } from "lucide-react";
import { ShinyWavyText } from "@/components/ShinyWavyText";
import { SponsorDialog } from "@/components/SponsorDialog";
import { Spinner } from "@/components/ui/spinner";
import { useAtomValue } from "jotai";
import { equippedTokenAtom } from "@/lib/atoms";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { EquipTokenSheetContent } from "@/components/EquipTokenSheetContent";
import { DefaultButton } from "@/components/DefaultButton";
import { FloatingLeftButton } from "@/components/FloatingLeftButton";
import ResourceBar from "@/components/ResourceBar";
import {
  CoinCollectBurst,
  type CoinCollectBurstHandle,
} from "@/components/CoinCollectBurst";
import ArenaChartECharts from "@/components/ArenaChartECharts";
import AnimatedWaiting from "@/components/AnimatedWaiting";
import { AvatarBorder } from "@/components/AvatarBorder";
import { UserName } from "@/components/UserName";
import { UserRankList } from "@/components/UserRankList";
import { UserRankCard } from "@/components/UserRankCard";
import { PrizeWrapper } from "@/components/PrizeWrapper";
import { Countdown } from "@/components/ui/countdown";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn, generateTextShadow, REWARD_ICONS, USDC_ICON } from "@/lib/utils";
import configs from "@/lib/configs";
import { useMusic } from "@/lib/hooks/useMusic";
import { MATCH_TRACK, MENU_TRACK } from "@/lib/configs/music";
import type {
  MatchmakingDetails,
  MatchmakingPlayer,
  MatchmakingQueueStatus,
  Player,
} from "@/lib/types/api";

type MatchSearch = {
  fromList?: boolean;
};

export const Route = createFileRoute("/(game)/match/$seed")({
  component: MatchPageWrapper,
  validateSearch: (search: Record<string, unknown>): MatchSearch => {
    return {
      fromList: search.fromList === "true" || search.fromList === true,
    };
  },
});

const MATCH_DURATION_SECONDS = 60;
// const RESULT_COIN_BURST_MAX_AGE_SECONDS = 86400; // WARN: For debugging only
const RESULT_COIN_BURST_MAX_AGE_SECONDS = 60;
const RESULT_COIN_BURST_LAYOUT_DELAY_MS = 200;
const RESULT_COIN_BURST_RETRY_MS = 1500;
const RESULT_COIN_BURST_MAX_COINS = 24;

function getResultCoinCount(winnerPayout: number) {
  return Math.min(
    RESULT_COIN_BURST_MAX_COINS,
    Math.max(0, Math.ceil(winnerPayout)),
  );
}

// Force a full remount when the seed param changes so SSE subscriptions,
// query caches, and ended-view state reset cleanly. Without this, tapping
// "Find Match" on a finished match navigates to a new /match/$seed but the
// component stays mounted and keeps showing the old ended view.
function MatchPageWrapper() {
  const { seed } = Route.useParams();
  return <MatchPage key={seed} />;
}

function MatchPage() {
  const { seed } = Route.useParams();
  const search = Route.useSearch();
  const router = useRouter();
  const navigate = useNavigate();
  const { data: details, isLoading, error } = useMatchmakingDetails(seed);
  const sse = useMatchmakingSse(seed);
  const { removeSeed } = useActiveSeeds();
  const usdcTargetRef = useRef<HTMLDivElement | null>(null);
  const coinBurstRef = useRef<CoinCollectBurstHandle | null>(null);
  const resultPrizeSourceRef = useRef<HTMLDivElement | null>(null);
  const triggeredResultBurstKeyRef = useRef<string | null>(null);
  const [isUsdcCollecting, setIsUsdcCollecting] = useState(false);
  const [isUsdcTargetReady, setIsUsdcTargetReady] = useState(false);

  const handleCoinCollectStart = useCallback(() => {
    setIsUsdcCollecting(true);
  }, []);

  const handleCoinCollectEnd = useCallback(() => {
    setIsUsdcCollecting(false);
  }, []);

  const handleResultPrizeCollect = useCallback(
    (source: HTMLElement, triggerKey: string, coinCount: number) => {
      if (triggeredResultBurstKeyRef.current === triggerKey) return true;

      const didSpawn =
        coinBurstRef.current?.spawnFromElement(source, { coinCount }) ?? false;

      if (didSpawn) {
        triggeredResultBurstKeyRef.current = triggerKey;
      }

      return didSpawn;
    },
    [],
  );

  const handleBack = useCallback(() => {
    if (search?.fromList) {
      router.history.back();
    } else {
      navigate({ to: "/home" });
    }
  }, [search?.fromList, router, navigate]);

  // Prefer the most recent snapshot from SSE; fall back to initial /details query
  const view: MatchmakingDetails | null =
    sse.snapshot ?? (details as MatchmakingDetails | undefined) ?? null;

  // Queue exists but was cancelled / expired → show cancelled UI (no match row was ever created).
  if (
    !view &&
    sse.queue &&
    (sse.queue.status === "cancelled" || sse.queue.status === "expired")
  ) {
    return (
      <div
        className="flex flex-col w-full h-full relative overflow-hidden bg-black"
        style={{ imageRendering: "pixelated" }}
      >
        <FloatingLeftButton
          onClick={handleBack}
          className="absolute left-0 top-4 z-50"
          icon={
            <svg
              className="w-4 h-4"
              viewBox="0 0 9 8"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M3 1V0H2V1H1V2H0V3H1V4H2V5H3V4H6V7H3V8H7V7H8V6H9V3H8V2H7V1H3Z"
                fill="white"
              />
            </svg>
          }
        />
        <ResourceBar
          className="absolute top-4 left-14 right-4 flex z-50 pl-3"
          showLuckyDrawTicket={false}
          showMenu={false}
          usdcTargetRef={usdcTargetRef}
          isUsdcCollecting={isUsdcCollecting}
          onUsdcTargetReadyChange={setIsUsdcTargetReady}
        />
        <CoinCollectBurst
          ref={coinBurstRef}
          targetRef={usdcTargetRef}
          onCollectStart={handleCoinCollectStart}
          onCollectEnd={handleCoinCollectEnd}
        />
        <QueueCancelledView
          status={sse.queue.status}
          onBackToLobby={() => {
            removeSeed(seed);
            navigate({ to: "/home" });
          }}
        />
      </div>
    );
  }

  // Pre-pair host state: SSE has an active queue but no match row yet → show searching UI.
  if (!view && sse.queue) {
    return (
      <div
        className="flex flex-col w-full h-full relative overflow-hidden bg-black"
        style={{ imageRendering: "pixelated" }}
      >
        <FloatingLeftButton
          onClick={handleBack}
          className="absolute left-0 top-4 z-50"
          icon={
            <svg
              className="w-4 h-4"
              viewBox="0 0 9 8"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M3 1V0H2V1H1V2H0V3H1V4H2V5H3V4H6V7H3V8H7V7H8V6H9V3H8V2H7V1H3Z"
                fill="white"
              />
            </svg>
          }
        />
        <ResourceBar
          className="absolute top-4 left-14 right-4 flex z-50 pl-3"
          showLuckyDrawTicket={false}
          showMenu={false}
          usdcTargetRef={usdcTargetRef}
          isUsdcCollecting={isUsdcCollecting}
          onUsdcTargetReadyChange={setIsUsdcTargetReady}
        />
        <CoinCollectBurst
          ref={coinBurstRef}
          targetRef={usdcTargetRef}
          onCollectStart={handleCoinCollectStart}
          onCollectEnd={handleCoinCollectEnd}
        />
        <SearchingView
          seed={seed}
          queuedAtEpoch={sse.queue.queuedAtEpoch}
          queueStatus={sse.queue.status}
          onCancelled={() => removeSeed(seed)}
        />
      </div>
    );
  }

  if (isLoading && !view && !sse.queue) {
    return <CenteredMessage title="Loading match…" />;
  }

  if (error && !view && !sse.queue) {
    const errMessage = extractError(error);
    return (
      <CenteredMessage
        title="Match unavailable"
        description={errMessage}
        action={
          <DefaultButton
            variant="green"
            onClick={() => navigate({ to: "/home" })}
          >
            Back to lobby
          </DefaultButton>
        }
      />
    );
  }

  if (!view) {
    return <CenteredMessage title="Connecting…" />;
  }

  const status = view.status;

  return (
    <div
      className="flex flex-col w-full h-full relative overflow-hidden bg-black"
      style={{ imageRendering: "pixelated" }}
    >
      <FloatingLeftButton
        onClick={handleBack}
        className="absolute left-0 top-4 z-50"
        icon={
          <svg
            className="w-4 h-4"
            viewBox="0 0 9 8"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M3 1V0H2V1H1V2H0V3H1V4H2V5H3V4H6V7H3V8H7V7H8V6H9V3H8V2H7V1H3Z"
              fill="white"
            />
          </svg>
        }
      />
      <ResourceBar
        className="absolute top-4 left-14 right-4 flex z-50 pl-3"
        showLuckyDrawTicket={false}
        showMenu={false}
        usdcTargetRef={usdcTargetRef}
        isUsdcCollecting={isUsdcCollecting}
        onUsdcTargetReadyChange={setIsUsdcTargetReady}
      />
      <CoinCollectBurst
        ref={coinBurstRef}
        targetRef={usdcTargetRef}
        onCollectStart={handleCoinCollectStart}
        onCollectEnd={handleCoinCollectEnd}
      />

      {status === "waiting" &&
        (view.players.length > 0 ? (
          // Match row exists with both players → 5s prep window
          <PrepView view={view} sse={sse} />
        ) : (
          // Queue-only state (host waiting for opponent) → searching UI
          <SearchingView
            seed={seed}
            queuedAtEpoch={sse.queue?.queuedAtEpoch}
            queueStatus={sse.queue?.status}
            onCancelled={() => removeSeed(seed)}
          />
        ))}
      {status === "live" && <LiveView view={view} />}
      {status === "completed" && (
        <ResultView
          view={view}
          seed={seed}
          isUsdcTargetReady={isUsdcTargetReady}
          usdcPrizeRef={resultPrizeSourceRef}
          onResultPrizeCollect={handleResultPrizeCollect}
        />
      )}
      {status === "cancelled" && <CancelledView seed={seed} view={view} />}
    </div>
  );
}

// ============ Adapters ============

function adaptPlayer(mp: MatchmakingPlayer, index: number): Player {
  return {
    squadAddress: mp.squadAddress,
    squadDetails: {
      name: mp.squadDetails.name,
      isBot: mp.squadDetails.isBot,
      index,
      formation: mp.squadDetails.formation,
      squadValue: mp.squadDetails.squadValue,
      squadMatchType: mp.squadDetails.squadMatchType,
      owner: mp.squadDetails.owner,
      entryType: 0,
      dna: mp.squadDetails.dna,
      orderPlayer: 0,
      playerDetails: mp.squadDetails.playerDetails.map((p) => ({
        playerName: p.playerName,
        assetType: p.assetType,
        playerSymbol: p.playerSymbol,
        playerImageUrl: p.playerImageUrl,
        pythLazerId: p.pythLazerId,
        playerAllocation: p.playerAllocation,
        positionIndex: p.positionIndex,
        priceChange24h: 0,
        priceChange7d: p.priceChange7d,
        currentPrice: p.currentPrice,
        startPrice: p.startPrice,
        endPrice: 0,
        pnlPercentage: p.pnlPercentage,
        ephemeralPriceFeedAccount: "",
        playerValue: p.playerValue,
        multiplier: p.multiplier,
        playerPosition: p.playerPosition,
        playerMarketCap: 0,
        playerPower: p.playerPower,
        playerCard: p.playerCard,
      })),
    },
    ownerProfile: {
      userId: mp.ownerProfile.userId,
      profilePicture: mp.ownerProfile.profilePicture,
      username: mp.ownerProfile.username,
      userPubkey: mp.squadDetails.owner,
      domainType:
        mp.ownerProfile.domainType === "seeker" ||
        mp.ownerProfile.domainType === "world"
          ? mp.ownerProfile.domainType
          : null,
      isLegends: mp.ownerProfile.isLegends,
      isVerified: mp.ownerProfile.isVerified,
      isDoubleTrophyActive: mp.ownerProfile.isDoubleTrophyActive,
      activeFrameUrl: mp.ownerProfile.activeFrameUrl ?? "",
      socialConnected: mp.ownerProfile.socialConnected,
      activeBorderChartId: mp.ownerProfile.activeBorderChartId,
      activeBorderChartUrl: mp.ownerProfile.activeBorderChartUrl,
      activeBorderChartName: mp.ownerProfile.activeBorderChartName,
      borderChartBaseColor: mp.ownerProfile.borderChartBaseColor,
      borderChartLineType:
        mp.ownerProfile.borderChartLineType === "solid" ||
        mp.ownerProfile.borderChartLineType === "dashed" ||
        mp.ownerProfile.borderChartLineType === "dotted"
          ? mp.ownerProfile.borderChartLineType
          : undefined,
    },
  };
}

// ============ Views ============

function SearchingView({
  seed,
  queuedAtEpoch,
  queueStatus,
  onCancelled,
}: {
  seed: string;
  queuedAtEpoch?: number;
  queueStatus?: MatchmakingQueueStatus;
  onCancelled: () => void;
}) {
  // While the host's create tx is still confirming on-chain (`pending_create`),
  // BE rejects cancel — show a non-actionable "Confirming…" interstitial
  // instead of the cancel button.
  const isConfirming = queueStatus === "pending_create";
  // BE epoch from SSE is authoritative (survives reload/close);
  // local mount epoch is the fallback for the brief pre-SSE moment.
  const localFallback = useSearchStartEpoch(seed);
  const startEpoch = queuedAtEpoch ?? localFallback;
  const [now, setNow] = useState(() => Math.floor(Date.now() / 1000));
  const cancelMutation = useMatchmakingCancel();
  const { recordRefundTx } = useCancelRefundTxs();
  const { user } = usePrivy();
  const { data: statsData } = useStats(user?.wallet?.address ?? "");
  const showCancelButton = false;

  useEffect(() => {
    const id = window.setInterval(
      () => setNow(Math.floor(Date.now() / 1000)),
      500,
    );
    return () => window.clearInterval(id);
  }, []);

  const elapsed = Math.max(0, now - startEpoch);

  const handleCancel = async () => {
    try {
      const resp = await cancelMutation.mutateAsync({ seed });
      recordRefundTx(seed, resp.refundTxSig);
      onCancelled();
      toast.success("Cancelled", {
        description: "Your entry has been refunded.",
      });
    } catch (err) {
      switch (classifyMatchmakingCancelError(err)) {
        case "confirming":
          toast.info("Confirming transaction", {
            description:
              "Hang tight — your entry is still confirming on-chain.",
          });
          return;
        case "reserving":
          toast.warning("Someone is challenging you", {
            description: "Hold on a few seconds and try again.",
          });
          return;
        case "challenge_committed":
          toast.info("Match starting", {
            description: "An opponent just joined — get ready.",
          });
          return;
        case "paired":
          toast.error("Cannot cancel", {
            description:
              "The match is already paired — it will run to finalize.",
          });
          return;
        default:
          toast.error("Cancel failed", { description: extractError(err) });
      }
    }
  };

  return (
    <>
      {/* Top: title + timer + you-vs-placeholder cards */}
      <div className="flex-1 max-h-[60%] flex flex-col items-center justify-center gap-6 px-4 home-wallpaper-background">
        <AnimatedWaiting
          prefix={
            isConfirming ? "Confirming transaction" : "Searching for opponent"
          }
          className="text-white text-xl sm:text-2xl font-black uppercase tracking-wider"
          useTextShadow
          textShadowSize={1}
        />
        {!isConfirming && (
          <div className="flex items-center gap-2">
            <img
              src="https://cdn.navalabs.io/icons/timer_rqnxy6.png"
              alt="timer"
              className="w-3.5 sm:w-4"
            />
            <span
              className="font-extrabold text-yellow-500 text-xl sm:text-2xl tracking-wider tabular-nums"
              style={{ textShadow: generateTextShadow(0.5) }}
            >
              {formatElapsed(elapsed)}
            </span>
          </div>
        )}

        {/* You vs Opponent — bordered cards, blue palette */}
        <div className="flex items-center gap-3">
          {/* You (filled) */}
          <div className="w-36 h-36 bg-[#1d2b53] border-2 border-[#56C3FE] rounded-md flex items-center justify-center">
            <div className="flex flex-col items-center gap-2">
              <AvatarBorder
                src={
                  statsData?.profilePicture ??
                  (user?.wallet?.address
                    ? `https://avatar.vercel.sh/${user.wallet.address}`
                    : "")
                }
                decoration={statsData?.activeFrameUrl}
                size={64}
              />
              <UserName
                username={statsData?.username ?? "You"}
                className="text-white font-bold text-xs max-w-[100px] justify-center"
                isVerified={statsData?.isVerified}
                domainType={statsData?.domainType}
                textShadow
              />
            </div>
          </div>

          {/* VS */}
          <span
            className="text-white text-3xl font-black uppercase tracking-wider"
            style={{ textShadow: generateTextShadow(0.7) }}
          >
            VS
          </span>

          {/* Opponent (placeholder w/ mascot) */}
          <div className="w-36 h-36 bg-white/5 border-2 border-white/15 border-dashed rounded-md flex items-center justify-center animate-pulse">
            <div className="flex flex-col items-center gap-2">
              <img
                src="https://cdn.navalabs.io/assets/token-mascot-TEMP_euuVaq.png"
                alt="opponent placeholder"
                className="w-16 h-16 object-contain opacity-80"
                style={{ imageRendering: "pixelated" }}
              />
              <span
                className="text-white/70 text-xs font-bold uppercase tracking-wider"
                style={{ textShadow: generateTextShadow(0.4) }}
              >
                Waiting
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom: helper text + Cancel control panel */}
      <div className="flex-1 max-h-[40svh] flex flex-col px-2 bg-[#1D2A52] border-t-4 border-[#0659B5] overflow-visible">
        <div className="flex-1 flex flex-col items-center justify-center gap-6 py-6">
          <p
            className="text-white/80 text-sm text-center max-w-md px-4"
            style={{ textShadow: generateTextShadow(0.3) }}
          >
            {isConfirming
              ? "Your entry is confirming on-chain. This usually takes a few seconds — you can leave this screen safely."
              : "We're matching you with another player. You can leave this screen and come back any time — your search keeps running."}
          </p>
          {showCancelButton && !isConfirming && (
            <DefaultButton
              variant="red"
              className="w-fit h-10 uppercase text-sm px-6!"
              onClick={handleCancel}
              disabled={cancelMutation.isPending}
            >
              {cancelMutation.isPending ? "Cancelling…" : "Cancel"}
            </DefaultButton>
          )}
        </div>
      </div>
    </>
  );
}

/**
 * Shown when a queue row exists but was cancelled / expired before pairing.
 * No matchmaking row exists, so we can't surface a refund tx (BE doesn't
 * persist it on the queue row). Just confirm the refund and let the user back out.
 */
function QueueCancelledView({
  status,
  onBackToLobby,
}: {
  status: "cancelled" | "expired";
  onBackToLobby: () => void;
}) {
  const isExpired = status === "expired";
  return (
    <div className="flex-1 flex flex-col home-wallpaper-background">
      <div className="flex-1 flex flex-col items-center justify-center gap-4 px-4 pb-6">
        <h1
          className="text-white text-3xl font-black uppercase tracking-wider text-center"
          style={{ textShadow: generateTextShadow(1) }}
        >
          {isExpired ? "Search expired" : "Match cancelled"}
        </h1>
        <p
          className="text-white/80 text-sm text-center max-w-md"
          style={{ textShadow: generateTextShadow(0.3) }}
        >
          {isExpired
            ? "Your search expired before an opponent joined."
            : "Your search was cancelled. Your entry has been refunded."}
        </p>
        <DefaultButton
          variant="green"
          className="h-12! px-6!"
          onClick={onBackToLobby}
        >
          Back to lobby
        </DefaultButton>
      </div>
    </div>
  );
}

function formatElapsed(seconds: number) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
}

function PrepView({
  view,
  sse,
}: {
  view: MatchmakingDetails;
  sse: ReturnType<typeof useMatchmakingSse>;
}) {
  const { user } = usePrivy();
  const { data: statsData } = useStats(user?.wallet?.address ?? "");
  const gameWalletAddress = statsData?.embeddedWallet;
  const [now, setNow] = useState(() => Math.floor(Date.now() / 1000));

  useEffect(() => {
    const id = window.setInterval(
      () => setNow(Math.floor(Date.now() / 1000)),
      500,
    );
    return () => window.clearInterval(id);
  }, []);

  // Fallback chain for the live-at epoch:
  //   1. SSE `paired` event (authoritative)
  //   2. view.createdDate + 2 (BE returns createdDate = pairedAt + 3, liveAt = pairedAt + 5)
  const liveAt =
    sse.pair?.liveAtEpoch ?? (view.createdDate ? view.createdDate + 2 : 0);
  const remaining = liveAt > 0 ? Math.max(0, liveAt - now) : null;

  // Player roster fallback chain:
  //   1. snapshot.players (post-snapshot state)
  //   2. paired event's host + challenger payload
  const players: MatchmakingPlayer[] =
    view.players.length > 0
      ? view.players
      : ([sse.pair?.host, sse.pair?.challenger].filter(
          Boolean,
        ) as MatchmakingPlayer[]);

  // Order: you on the left, opponent on the right
  const ordered = [...players].sort((a, b) => {
    if (gameWalletAddress && a.squadDetails.owner === gameWalletAddress)
      return -1;
    if (gameWalletAddress && b.squadDetails.owner === gameWalletAddress)
      return 1;
    return 0;
  });
  const you = ordered[0];
  const opponent = ordered[1];

  return (
    <>
      {/* Top: title + countdown + cards */}
      <div className="flex-1 max-h-[60%] flex flex-col items-center justify-center gap-6 px-4 home-wallpaper-background">
        <AnimatedWaiting
          prefix="Match found"
          className="text-white text-3xl font-black uppercase tracking-wider"
          useTextShadow
          textShadowSize={1}
        />
        <div className="flex items-center gap-2">
          <img
            src="https://cdn.navalabs.io/icons/timer_rqnxy6.png"
            alt="timer"
            className="w-3.5 sm:w-4"
          />
          <span
            className="font-extrabold text-yellow-500 text-xl sm:text-2xl tracking-wider tabular-nums"
            style={{ textShadow: generateTextShadow(0.5) }}
          >
            {remaining !== null ? `Starts in ${remaining}s` : "Get ready…"}
          </span>
        </div>

        {/* You vs Opponent — bordered cards (blue palette) */}
        <div className="flex items-center gap-3">
          <PrepPlayerCard player={you} />
          <span
            className="text-white text-3xl font-black uppercase tracking-wider"
            style={{ textShadow: generateTextShadow(0.7) }}
          >
            VS
          </span>
          <PrepPlayerCard player={opponent} />
        </div>
      </div>

      {/* Bottom: control panel — no Cancel button (paired matches always run to finalize) */}
      <div className="flex-1 max-h-[40svh] flex flex-col px-2 bg-[#1D2A52] border-t-4 border-[#0659B5] overflow-visible">
        <div className="flex-1 flex flex-col items-center justify-center gap-3 py-6">
          <h2 className="text-2xl font-bold text-yellow-300 uppercase tracking-wider">
            Get ready
          </h2>
          <p
            className="text-white/80 text-sm text-center max-w-md px-4"
            style={{ textShadow: generateTextShadow(0.3) }}
          >
            Both players are locked in. The match starts in a few seconds — no
            turning back now.
          </p>
        </div>
      </div>
    </>
  );
}

function PrepPlayerCard({ player }: { player: MatchmakingPlayer | undefined }) {
  if (!player) {
    // Should rarely render — paired state means both sides are known
    return (
      <div className="w-36 h-36 bg-white/5 border-2 border-white/15 border-dashed rounded-md" />
    );
  }
  return (
    <div className="w-36 h-36 bg-[#1d2b53] border-2 border-[#56C3FE] rounded-md flex items-center justify-center">
      <div className="flex flex-col items-center gap-2">
        <AvatarBorder
          src={player.ownerProfile.profilePicture}
          decoration={player.ownerProfile.activeFrameUrl ?? undefined}
          size={64}
        />
        <UserName
          username={player.ownerProfile.username}
          className="text-white font-bold text-xs max-w-[100px] justify-center"
          isVerified={player.ownerProfile.isVerified}
          domainType={
            player.ownerProfile.domainType === "seeker" ||
            player.ownerProfile.domainType === "world"
              ? player.ownerProfile.domainType
              : null
          }
          textShadow
        />
      </div>
    </div>
  );
}

function LiveView({ view }: { view: MatchmakingDetails }) {
  const { user } = usePrivy();
  const { data: statsData } = useStats(user?.wallet?.address ?? "");
  const gameWalletAddress = statsData?.embeddedWallet;

  const { play } = useMusic();
  useEffect(() => {
    play({ ...MATCH_TRACK, autoPlay: true });
    return () => {
      play({ ...MENU_TRACK, autoPlay: true });
    };
  }, [play]);

  const adaptedPlayers = useMemo(
    () => view.players.map((p, i) => adaptPlayer(p, i)),
    [view.players],
  );

  const [liveSquadValues, setLiveSquadValues] = useState<Map<string, number>>(
    new Map(),
  );

  // matchStartedAt is in ms — endTimestamp is epoch seconds
  const matchStartedAt = view.endTimestamp
    ? (view.endTimestamp - MATCH_DURATION_SECONDS) * 1000
    : undefined;

  return (
    <>
      {/* Top: chart + countdown */}
      <div className="relative max-h-[60%] flex-1 flex flex-col overflow-hidden gap-2 pt-14">
        <div className="absolute top-16 w-full flex flex-col items-center justify-center z-10">
          <p className="text-sm text-white leading-4 uppercase">Ending in</p>
          <div className="flex items-center justify-center rounded-full">
            <Countdown
              className="font-extrabold text-yellow-400 text-xl tracking-wider"
              startTimestamp={Number(view.endTimestamp)}
            />
          </div>
        </div>

        <ArenaChartECharts
          players={adaptedPlayers}
          matchStartedAt={matchStartedAt}
          onSquadValuesUpdate={setLiveSquadValues}
        />
      </div>

      {/* Bottom: 2-player live control panel */}
      <MatchLiveControl
        view={view}
        liveSquadValues={liveSquadValues}
        gameWalletAddress={gameWalletAddress ?? null}
        className="h-full max-h-[40svh]"
      />
    </>
  );
}

function MatchLiveControl({
  view,
  liveSquadValues,
  gameWalletAddress,
  className,
}: {
  view: MatchmakingDetails;
  liveSquadValues: Map<string, number>;
  gameWalletAddress: string | null;
  className?: string;
}) {
  // Sort by live rank (winner first). Tie → host (players[0]) wins.
  const orderedPlayers = useMemo(() => {
    const valueOf = (p: MatchmakingPlayer) =>
      liveSquadValues.get(p.squadDetails.dna) ?? p.squadDetails.squadValue;
    return [...view.players].sort((a, b) => {
      const av = valueOf(a);
      const bv = valueOf(b);
      if (av !== bv) return bv - av;
      if (view.players[0]?.squadAddress === a.squadAddress) return -1;
      if (view.players[0]?.squadAddress === b.squadAddress) return 1;
      return 0;
    });
  }, [view.players, liveSquadValues]);

  // Live rank lookup matches the ordered list.
  const liveRanks = useMemo(() => {
    const map = new Map<string, number>();
    orderedPlayers.forEach((p, i) => map.set(p.squadAddress, i + 1));
    return map;
  }, [orderedPlayers]);

  // Live "is the user currently winning?" — recomputes as squad values tick.
  // Tie-break: host wins (matchmaking rule).
  const selfPlayer = gameWalletAddress
    ? view.players.find((p) => p.squadDetails.owner === gameWalletAddress)
    : null;
  const isUserCurrentlyWinning = useMemo(() => {
    if (!selfPlayer) return false;
    const opponentPlayer = view.players.find(
      (p) => p.squadAddress !== selfPlayer.squadAddress,
    );
    if (!opponentPlayer) return false;

    const selfValue =
      liveSquadValues.get(selfPlayer.squadDetails.dna) ??
      selfPlayer.squadDetails.squadValue;
    const oppValue =
      liveSquadValues.get(opponentPlayer.squadDetails.dna) ??
      opponentPlayer.squadDetails.squadValue;

    // Host index in players[] is 0 per API contract
    const isHost = view.players[0]?.squadAddress === selfPlayer.squadAddress;
    if (selfValue > oppValue) return true;
    if (selfValue < oppValue) return false;
    return isHost; // tie → host wins
  }, [selfPlayer, view.players, liveSquadValues]);

  const winnerTrophies = view.trophies?.trophiesWin ?? 0;
  const loserTrophies = view.trophies?.trophiesLost ?? 0;

  const potentialPrizes = [
    {
      icon: USDC_ICON,
      // String "0" survives PrizeWrapper's `value !== 0` filter so the USDC
      // logo stays visible when the user is currently losing.
      value: isUserCurrentlyWinning ? view.winnerPayout : "0",
      alt: "USDC",
      isPositive: true,
    },
    ...(isUserCurrentlyWinning && winnerTrophies > 0
      ? [
          {
            icon: REWARD_ICONS[1],
            value: winnerTrophies,
            alt: "Trophy",
            isPositive: true,
          },
        ]
      : !isUserCurrentlyWinning && loserTrophies > 0
        ? [
            {
              icon: REWARD_ICONS[1],
              value: loserTrophies,
              alt: "Trophy",
              isPositive: true,
            },
          ]
        : []),
  ];

  return (
    <div
      className={cn(
        "flex flex-col gap-2 px-2 py-2 bg-[#1D2A52] border-t-4 border-[#0659B5] overflow-y-auto",
        className,
      )}
    >
      {selfPlayer && potentialPrizes.length > 0 && (
        <div className="dark-blue-container flex items-center justify-between my-1 px-4 py-1">
          <p>Your potential earnings</p>
          <PrizeWrapper
            prizes={potentialPrizes}
            iconSize="size-8"
            textSize="text-base"
            className="py-0 gap-2 scale-80"
          />
        </div>
      )}
      {orderedPlayers.map((mp, i) => {
        const adapted = adaptPlayer(mp, i);
        const liveValue =
          liveSquadValues.get(mp.squadDetails.dna) ??
          mp.squadDetails.squadValue;
        const isSelf =
          !!gameWalletAddress && mp.squadDetails.owner === gameWalletAddress;
        const rank = liveRanks.get(mp.squadAddress) ?? i + 1;
        return (
          <UserRankCard
            key={mp.squadAddress}
            player={adapted}
            rank={rank}
            isHighlighted={isSelf}
            isWinner={rank === 1}
            liveSquadValue={liveValue}
            matchEndTimestamp={view.endTimestamp}
          />
        );
      })}
    </div>
  );
}

function ResultView({
  view,
  seed,
  isUsdcTargetReady,
  usdcPrizeRef,
  onResultPrizeCollect,
}: {
  view: MatchmakingDetails;
  seed: string;
  isUsdcTargetReady: boolean;
  usdcPrizeRef: RefObject<HTMLDivElement | null>;
  onResultPrizeCollect: (
    source: HTMLElement,
    triggerKey: string,
    coinCount: number,
  ) => boolean;
}) {
  const navigate = useNavigate();
  const { user } = usePrivy();
  const { data: statsData } = useStats(user?.wallet?.address ?? "");
  const gameWalletAddress = statsData?.embeddedWallet;
  const { data: usdcBalance } = useBalance(gameWalletAddress ?? "");

  // Shared matchmaking flow — same behavior as the home PLAY button. When the
  // user has no equipped token we send them back to /home to pick one.
  const {
    findMatch,
    disabled: findMatchDisabled,
    isSubmitting: isFindingMatch,
    isSponsoredEligible: isFindMatchSponsored,
    isPreviewingSponsor,
    isFreeConfirmOpen,
    previewedSponsor,
    confirmFreeMatch,
    dismissFreeConfirm,
  } = useFindMatch({
    onMissingEquip: () => navigate({ to: "/home" }),
  });

  // Report mode + dialog
  const [isReportMode, setIsReportMode] = useState(false);
  const [reportTarget, setReportTarget] = useState<Player | null>(null);
  // Clicking a row reveals that squad's details in the bottom panel.
  const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null);
  const [selectedPlayerRank, setSelectedPlayerRank] = useState<number | null>(
    null,
  );
  const reportedIds = view.players
    .map((p) => p.ownerProfile.userId)
    .filter((id): id is number => id != null);
  const { data: canReportStatuses } = useCanReportBatch(
    reportedIds,
    statsData?.userId,
  );
  const canReportMap = new Map(
    canReportStatuses?.map((s) => [s.reportedId, s.canReport]) ?? [],
  );

  const winnerPlayerFromSquad = view.winner.squadWinner
    ? view.players.find((p) => p.squadAddress === view.winner.squadWinner)
    : null;
  const winnerPlayerFromOwner = view.winner.squadOwner
    ? view.players.find((p) => p.squadDetails.owner === view.winner.squadOwner)
    : null;
  const winnerPlayer = winnerPlayerFromSquad ?? winnerPlayerFromOwner ?? null;
  const winnerSquadAddress =
    view.winner.squadWinner ?? winnerPlayer?.squadAddress ?? null;
  const winnerOwnerAddress =
    view.winner.squadOwner ?? winnerPlayer?.squadDetails.owner ?? null;

  // Sort: winner first, loser second
  const sortedPlayers = useMemo(() => {
    return [...view.players].sort((a, b) => {
      if (a.squadAddress === winnerSquadAddress) return -1;
      if (b.squadAddress === winnerSquadAddress) return 1;
      return b.squadDetails.squadValue - a.squadDetails.squadValue;
    });
  }, [view.players, winnerSquadAddress]);

  const matchEpoch = view.endTimestamp || view.createdDate;
  const dateLine = matchEpoch
    ? dayjs.unix(matchEpoch).format("MMMM Do, YYYY · HH:mm")
    : null;

  // Determine self / outcome
  const selfPlayer = gameWalletAddress
    ? view.players.find((p) => p.squadDetails.owner === gameWalletAddress)
    : null;
  const selfRank = selfPlayer
    ? sortedPlayers.findIndex(
        (p) => p.squadAddress === selfPlayer.squadAddress,
      ) + 1
    : 0;
  const isSelfWinner =
    !!selfPlayer &&
    !!winnerOwnerAddress &&
    selfPlayer.squadDetails.owner === winnerOwnerAddress;
  const matchEndedAt = view.endTimestamp ?? view.createdDate ?? null;
  const resultCoinCount = getResultCoinCount(view.winnerPayout);
  const missedBy =
    selfPlayer && winnerPlayer && !isSelfWinner
      ? Math.max(
          0,
          winnerPlayer.squadDetails.squadValue -
            selfPlayer.squadDetails.squadValue,
        )
      : null;

  useEffect(() => {
    if (
      !isSelfWinner ||
      !isUsdcTargetReady ||
      resultCoinCount === 0 ||
      !matchEndedAt
    ) {
      return;
    }

    const matchAgeSeconds = Math.floor(Date.now() / 1000) - matchEndedAt;
    if (matchAgeSeconds >= RESULT_COIN_BURST_MAX_AGE_SECONDS) return;

    const triggerKey = `${seed}:${matchEndedAt}`;
    let frameId: number | null = null;
    let retryUntil = 0;

    const attemptSpawn = () => {
      const latestMatchAgeSeconds =
        Math.floor(Date.now() / 1000) - matchEndedAt;
      if (latestMatchAgeSeconds >= RESULT_COIN_BURST_MAX_AGE_SECONDS) return;

      const source = usdcPrizeRef.current;
      if (source && onResultPrizeCollect(source, triggerKey, resultCoinCount)) {
        return;
      }

      if (performance.now() < retryUntil) {
        frameId = window.requestAnimationFrame(attemptSpawn);
      }
    };

    const timeoutId = window.setTimeout(() => {
      retryUntil = performance.now() + RESULT_COIN_BURST_RETRY_MS;
      attemptSpawn();
    }, RESULT_COIN_BURST_LAYOUT_DELAY_MS);

    return () => {
      window.clearTimeout(timeoutId);
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }
    };
  }, [
    isSelfWinner,
    isUsdcTargetReady,
    matchEndedAt,
    onResultPrizeCollect,
    resultCoinCount,
    seed,
    usdcPrizeRef,
  ]);

  return (
    <>
      {/* Top: header + ranked players */}
      <div
        className="relative flex-1 max-h-[60%] flex flex-col h-full justify-between gap-2 overflow-hidden p-1"
        style={{
          backgroundImage: `url('https://cdn.navalabs.io/assets/bg-full_cJmDRn.png')`,
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
          imageRendering: "pixelated",
        }}
      >
        <ReportModeToggle
          isActive={isReportMode}
          onToggle={() => setIsReportMode((prev) => !prev)}
        />
        <div className="flex flex-col items-center gap-1 mt-4 px-4">
          <h1
            className="text-white text-3xl font-black uppercase tracking-wider"
            style={{ textShadow: generateTextShadow(1) }}
          >
            1v1
          </h1>
          {dateLine && (
            <span
              className="text-white text-sm font-bold uppercase"
              style={{ textShadow: generateTextShadow(0.5) }}
            >
              {dateLine}
            </span>
          )}
          <div className="flex items-center gap-1.5 mt-0.5">
            <span
              className="text-white text-sm font-bold uppercase"
              style={{ textShadow: generateTextShadow(0.5) }}
            >
              Entry fee:
            </span>
            <img
              src={REWARD_ICONS[0]}
              alt="USDC"
              className="w-4 h-4"
              style={{ imageRendering: "pixelated" }}
            />
            <span
              className="text-white text-sm font-black"
              style={{ textShadow: generateTextShadow(0.5) }}
            >
              {view.entryUsdc}
            </span>
          </div>
          {isReportMode && (
            <p
              className="text-white text-sm font-bold mt-1 uppercase animate-pulse"
              style={{ textShadow: generateTextShadow(1) }}
            >
              Select a player to report
            </p>
          )}
        </div>

        <ScrollArea className="flex-1 h-full! mt-4" type="auto">
          <div className="flex flex-col items-stretch gap-2 px-2 pb-4">
            {sortedPlayers.map((mp, index) => {
              const player = adaptPlayer(mp, index);
              const rank = index + 1;
              const isWinner = winnerSquadAddress
                ? mp.squadAddress === winnerSquadAddress
                : !!winnerOwnerAddress &&
                  mp.squadDetails.owner === winnerOwnerAddress;
              const isSelf =
                !!gameWalletAddress &&
                player.ownerProfile.userPubkey === gameWalletAddress;

              const prizes = isWinner
                ? [
                    {
                      icon: REWARD_ICONS[0],
                      value: view.winnerPayout,
                      isPositive: true,
                    },
                    ...(view.trophies?.trophiesWin
                      ? [
                          {
                            icon: REWARD_ICONS[1],
                            value: view.trophies.trophiesWin,
                            isPositive: true,
                          },
                        ]
                      : []),
                  ]
                : view.trophies?.trophiesLost
                  ? [
                      {
                        icon: REWARD_ICONS[1],
                        value: view.trophies.trophiesLost,
                        isPositive: true,
                      },
                    ]
                  : [];

              return (
                <div
                  key={mp.squadAddress}
                  className={cn(
                    isReportMode && isSelf && "opacity-40 pointer-events-none",
                  )}
                >
                  <UserRankList
                    player={player}
                    rank={rank}
                    isWinner={isWinner}
                    prizes={prizes}
                    isSelf={isSelf}
                    usdcPrizeRef={
                      isWinner && isSelf && view.winnerPayout > 0
                        ? usdcPrizeRef
                        : undefined
                    }
                    canReport={
                      canReportMap.get(mp.ownerProfile.userId ?? -1) ?? false
                    }
                    isReportMode={isReportMode && !isSelf}
                    onClick={() => {
                      if (isReportMode) {
                        if (!isSelf) setReportTarget(player);
                      } else {
                        setSelectedPlayer(player);
                        setSelectedPlayerRank(rank);
                      }
                    }}
                  />
                </div>
              );
            })}
          </div>
        </ScrollArea>
      </div>

      {/* Bottom: ended control panel */}
      <MatchEndedControl
        isSpectator={!selfPlayer}
        isWinner={isSelfWinner}
        selfRank={selfRank}
        winnerPayout={view.winnerPayout}
        winnerTrophies={view.trophies?.trophiesWin ?? 0}
        loserTrophies={view.trophies?.trophiesLost ?? 0}
        missedBy={missedBy}
        payoutTxSignature={view.tracking?.distributeRewardsTxSignature ?? null}
        isDoubleTrophyActive={
          selfPlayer?.ownerProfile.isDoubleTrophyActive ?? false
        }
        onFindMatch={findMatch}
        isFindingMatch={isFindingMatch || isPreviewingSponsor}
        findMatchDisabled={findMatchDisabled}
        isFindMatchSponsored={isFindMatchSponsored}
        usdcBalance={usdcBalance}
        selectedPlayer={selectedPlayer}
        selectedPlayerRank={selectedPlayerRank}
        winnerSquadAddress={winnerSquadAddress}
        matchEndTimestamp={view.endTimestamp ?? null}
        selfWalletAddress={gameWalletAddress ?? null}
        onClearSelectedPlayer={() => {
          setSelectedPlayer(null);
          setSelectedPlayerRank(null);
        }}
        className="h-full max-h-[40svh]"
      />

      <SponsorDialog
        open={isFreeConfirmOpen}
        onOpenChange={(open) => {
          if (!open) dismissFreeConfirm();
        }}
        sponsor={
          previewedSponsor
            ? {
                name: previewedSponsor.name,
                wordmark: previewedSponsor.name,
                logoUrl: previewedSponsor.image_url,
                description: previewedSponsor.description,
              }
            : undefined
        }
        onConfirm={() => void confirmFreeMatch()}
        confirmLabel="Find Match"
        isConfirming={isFindingMatch}
      />

      <ReportPlayerDialog
        open={reportTarget !== null}
        onOpenChange={(open) => {
          if (!open) {
            setReportTarget(null);
            setIsReportMode(false);
          }
        }}
        reportedId={reportTarget?.ownerProfile.userId}
        reportedUsername={reportTarget?.ownerProfile.username}
        reportedProfilePicture={reportTarget?.ownerProfile.profilePicture}
        reportedFrameUrl={reportTarget?.ownerProfile.activeFrameUrl}
        reportedIsVerified={reportTarget?.ownerProfile.isVerified}
        canReportPlayer={
          canReportMap.get(reportTarget?.ownerProfile.userId ?? -1) ?? false
        }
        proofId={seed}
        playerDetails={reportTarget?.squadDetails.playerDetails}
      />
    </>
  );
}

/**
 * Compact pill showing the user's currently equipped asset + direction, sized
 * for the post-match action panel. Tapping it opens the equip sheet so the
 * user can swap the asset/direction before re-entering matchmaking.
 */
function FindMatchEquippedDisplay({ onClick }: { onClick?: () => void }) {
  const equipped = useAtomValue(equippedTokenAtom);
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex flex-col items-center gap-0 active:scale-[0.98] transition-transform bg-transparent border-none p-0 cursor-pointer"
    >
      {equipped ? (
        <img
          src={equipped.token.playerCard}
          alt={equipped.token.tokenSymbol || "Equipped"}
          className="w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32 object-contain"
          style={{ imageRendering: "pixelated" }}
        />
      ) : (
        <img
          src="/assets/ref-unknown.png"
          alt="Equip token"
          className="w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32 object-contain"
          style={{ imageRendering: "pixelated" }}
        />
      )}
      {equipped ? (
        <div
          className="relative -mt-3 flex items-stretch border-2 border-black/60 rounded overflow-hidden shadow-[0_2px_0_rgba(0,0,0,0.4)]"
          style={{ imageRendering: "pixelated" }}
        >
          <div className="px-2 py-0.5 bg-zinc-900 flex items-center gap-1">
            <img
              src={equipped.token.tokenImageLogo}
              alt=""
              className="size-3.5 rounded-full"
            />
            <span
              className="text-white text-xs font-black uppercase tracking-wider leading-none"
              style={{ textShadow: generateTextShadow(0.5) }}
            >
              {equipped.token.tokenSymbol}
            </span>
          </div>
          <div
            className={cn(
              "px-2 py-0.5 flex items-center gap-1",
              equipped.direction === "long" ? "bg-emerald-600" : "bg-red-600",
            )}
          >
            <span className="text-white text-xs font-black leading-none">
              {equipped.direction === "long" ? "▲" : "▼"}
            </span>
            <span
              className="text-white text-xs font-black uppercase tracking-wider leading-none"
              style={{ textShadow: generateTextShadow(0.5) }}
            >
              {equipped.direction}
            </span>
          </div>
        </div>
      ) : (
        <div
          className="relative -mt-6 px-2 py-0.5 bg-zinc-900 border-2 border-black/60 rounded shadow-[0_2px_0_rgba(0,0,0,0.4)]"
          style={{ imageRendering: "pixelated" }}
        >
          <span
            className="text-white text-xs font-black uppercase tracking-wider leading-none"
            style={{ textShadow: generateTextShadow(0.5) }}
          >
            Tap to Pick
          </span>
        </div>
      )}
    </button>
  );
}

function MatchEndedControl({
  isSpectator,
  isWinner,
  winnerPayout,
  missedBy,
  payoutTxSignature,
  onFindMatch,
  isFindingMatch,
  findMatchDisabled,
  isFindMatchSponsored,
  usdcBalance,
  selectedPlayer,
  selectedPlayerRank,
  winnerSquadAddress,
  matchEndTimestamp,
  selfWalletAddress,
  onClearSelectedPlayer,
  className,
}: {
  isSpectator: boolean;
  isWinner: boolean;
  selfRank: number;
  winnerPayout: number;
  winnerTrophies: number;
  loserTrophies: number;
  missedBy: number | null;
  payoutTxSignature: string | null;
  isDoubleTrophyActive: boolean;
  onFindMatch: (entryUsdc?: number) => void | Promise<void>;
  isFindingMatch: boolean;
  findMatchDisabled: boolean;
  isFindMatchSponsored: boolean;
  usdcBalance?: number;
  selectedPlayer?: Player | null;
  selectedPlayerRank?: number | null;
  winnerSquadAddress?: string | null;
  matchEndTimestamp?: number | null;
  selfWalletAddress?: string | null;
  onClearSelectedPlayer?: () => void;
  className?: string;
}) {
  const [isEquipSheetOpen, setIsEquipSheetOpen] = useState(false);
  const [entryFee, setEntryFee] = useState(3);
  const [isFeePopoverOpen, setIsFeePopoverOpen] = useState(false);

  // When the user taps a row in the rank list, swap the bottom panel for that
  // squad's details instead of the win/loss + Find Match block.
  if (selectedPlayer && onClearSelectedPlayer) {
    const isSelectedWinner =
      !!winnerSquadAddress &&
      selectedPlayer.squadAddress === winnerSquadAddress;
    const isSelectedSelf =
      !!selfWalletAddress &&
      selectedPlayer.squadDetails.owner === selfWalletAddress;
    return (
      <div
        className={cn(
          "flex flex-col px-2 pt-2 bg-[#1D2A52] border-t-4 border-[#0659B5] overflow-y-auto",
          className,
        )}
      >
        <DefaultButton
          onClick={onClearSelectedPlayer}
          className="flex items-center justify-center gap-1 max-w-fit mb-3"
        >
          <svg
            className="size-3"
            viewBox="0 0 17 36"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M12 0H17V36H12V30H6V24H0V12H6V6H12V0Z" fill="white" />
          </svg>
          <span className="mt-0.5">Back</span>
        </DefaultButton>
        <UserRankCard
          player={selectedPlayer}
          rank={selectedPlayerRank ?? 999}
          isWinner={isSelectedWinner}
          isHighlighted={isSelectedSelf}
          matchEndTimestamp={matchEndTimestamp ?? undefined}
        />
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex-1 flex flex-col px-2 pt-2 bg-[#1D2A52] border-t-4 border-[#0659B5] overflow-visible",
        className,
      )}
    >
      <div className="flex-1 flex flex-col items-center pt-3 pb-4 gap-4">
        {isSpectator ? (
          <SpectatorBlock winnerPayout={winnerPayout} />
        ) : isWinner ? (
          <WinBlock />
        ) : (
          <LossBlock missedBy={missedBy} />
        )}

        <div className="mt-auto flex flex-col items-center justify-center w-full gap-3">
          <FindMatchEquippedDisplay onClick={() => setIsEquipSheetOpen(true)} />
          <div className="relative flex items-center justify-center">
            <DefaultButton
              variant={isFindMatchSponsored ? "yellow" : "default"}
              className={cn(
                "h-10 uppercase text-sm",
                isFindMatchSponsored ? "w-44" : "w-64",
              )}
              onClick={() => {
                // Sponsored stays at $3 server-side — don't forward a tier.
                if (isFindMatchSponsored) {
                  void onFindMatch();
                  return;
                }
                if (typeof usdcBalance === "number" && usdcBalance < entryFee) {
                  toast.error("Not enough USDC", {
                    description: `Need $${entryFee} to play — your balance is $${usdcBalance.toFixed(2)}.`,
                  });
                  return;
                }
                void onFindMatch(entryFee);
              }}
              disabled={findMatchDisabled || isFindingMatch}
            >
              {isFindingMatch ? (
                <span className="w-full flex items-center justify-center">
                  <Spinner className="w-4 h-4" />
                </span>
              ) : isFindMatchSponsored ? (
                <span className="inline-flex items-center justify-center gap-1.5">
                  <span>Find Match</span>
                  <ShinyWavyText
                    text="FREE"
                    className="font-black text-sm uppercase tracking-wider -mt-2"
                    dy={2}
                  />
                </span>
              ) : (
                <span className="w-full h-full flex items-center">
                  <span className="flex-1 flex items-center justify-center whitespace-nowrap">
                    Find Match
                  </span>
                  <span
                    aria-hidden="true"
                    className="shrink-0 w-0.5 h-5 ml-2 mr-1 bg-black/45 shadow-[1px_0_0_rgba(255,255,255,0.18)]"
                  />
                  <span className="shrink-0 inline-flex items-center justify-center gap-1 leading-none px-1.5">
                    <img
                      src={USDC_ICON}
                      alt="USDC"
                      className="w-4 h-4 block"
                      style={{ imageRendering: "pixelated" }}
                    />
                    <span className="text-white text-sm font-black leading-none inline-block w-4 text-center tabular-nums">
                      {entryFee}
                    </span>
                    <ChevronDown
                      aria-hidden="true"
                      className="size-3 -ml-0.5 text-white/85"
                    />
                  </span>
                </span>
              )}
            </DefaultButton>
            {!isFindMatchSponsored && (
              <DropdownMenu
                open={isFeePopoverOpen}
                onOpenChange={setIsFeePopoverOpen}
              >
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    aria-label="Pick entry fee"
                    className="absolute right-0 top-0 z-30 h-10 w-20 cursor-pointer bg-transparent"
                    onClick={(e) => e.stopPropagation()}
                  />
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  side="top"
                  align="end"
                  sideOffset={6}
                  className="max-w-[16rem]"
                  onCloseAutoFocus={(e) => e.preventDefault()}
                >
                  <DropdownMenuLabel className="text-xs text-white/80 font-normal leading-snug whitespace-normal">
                    You'll be matched with another player who picks the same
                    entry fee.
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {[1, 3, 5, 10].map((v) => (
                    <DropdownMenuItem
                      key={v}
                      onSelect={() => setEntryFee(v)}
                      className="relative pl-9 pr-7 py-1.5 font-black cursor-pointer"
                    >
                      {entryFee === v && (
                        <span className="absolute left-2 flex size-3.5 items-center justify-center">
                          <img
                            src="https://cdn.navalabs.io/assets/arrow-select_yhEOlT.png"
                            alt=""
                            className="size-3"
                            style={{ imageRendering: "pixelated" }}
                          />
                        </span>
                      )}
                      <img
                        src={USDC_ICON}
                        alt=""
                        className="w-4 h-4"
                        style={{ imageRendering: "pixelated" }}
                      />
                      {v}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
          {isWinner && payoutTxSignature && (
            <a
              href={solscanTxUrl(payoutTxSignature)}
              target="_blank"
              rel="noreferrer"
              className="-mt-2 text-xs font-bold text-white/80 hover:cursor-pointer hover:text-white hover:underline active:scale-95 transition-all duration-300"
            >
              View prize distribution
            </a>
          )}
        </div>
      </div>
      <Sheet open={isEquipSheetOpen} onOpenChange={setIsEquipSheetOpen}>
        <SheetContent
          side="bottom"
          className="bg-[#1D2A52] border-t-2 border-[#3a4f8a] rounded-t-2xl flex flex-col max-h-[85svh] px-2 pb-[max(1rem,env(safe-area-inset-bottom))]"
        >
          <EquipTokenSheetContent
            onEquipped={() => setIsEquipSheetOpen(false)}
          />
        </SheetContent>
      </Sheet>
    </div>
  );
}

function WinBlock() {
  return (
    <>
      <div className="flex flex-col items-center justify-center">
        <h2 className="text-2xl font-bold text-yellow-500 uppercase">
          Congrats, you won!
        </h2>
        <p className="text-xs font-bold text-white/80 text-center">
          Keep the momentum going
        </p>
      </div>
    </>
  );
}

function LossBlock({ missedBy }: { missedBy: number | null }) {
  return (
    <>
      <div className="flex flex-col items-center justify-center">
        <h2 className="text-2xl font-bold text-white uppercase">
          Oof… so close
        </h2>
        {missedBy !== null && (
          <p className="text-xs font-bold text-white/80 text-center">
            You missed the top by {missedBy.toFixed(6)}% (squad PnL)
          </p>
        )}
      </div>
    </>
  );
}

function SpectatorBlock({ winnerPayout }: { winnerPayout: number }) {
  return (
    <div className="flex flex-col items-center justify-center gap-1">
      <h2 className="text-3xl font-bold text-white uppercase">Match ended</h2>
      <div className="flex items-center gap-1.5">
        <span className="text-sm font-bold text-white/80">Winner takes</span>
        <img
          src={REWARD_ICONS[0]}
          alt="USDC"
          className="w-4 h-4"
          style={{ imageRendering: "pixelated" }}
        />
        <span
          className="text-sm font-black text-white"
          style={{ textShadow: generateTextShadow(0.5) }}
        >
          +{winnerPayout}
        </span>
      </div>
    </div>
  );
}

function CancelledView({
  seed,
  view,
}: {
  seed: string;
  view: MatchmakingDetails;
}) {
  const navigate = useNavigate();
  const { refundTxs } = useCancelRefundTxs();
  // v0.1: paired matches are no longer cancellable, so `distributeRefunds*`
  // is gone from tracking. Refund tx for pre-pair cancels comes from the
  // local cancel-API response or the top-level `refundTxSignature`.
  const refundsTx = refundTxs[seed] ?? view.refundTxSignature ?? null;

  const matchEpoch = view.endTimestamp || view.createdDate;
  const dateLine = matchEpoch
    ? dayjs.unix(matchEpoch).format("MMMM Do, YYYY · HH:mm")
    : null;

  return (
    <>
      {/* Top: header + match details */}
      <div
        className="relative flex-1 max-h-[60%] flex flex-col h-full justify-between gap-2 overflow-hidden p-1"
        style={{
          backgroundImage: `url('https://cdn.navalabs.io/assets/bg-full_cJmDRn.png')`,
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
          imageRendering: "pixelated",
        }}
      >
        <div className="flex flex-col items-center gap-1 mt-16 px-4">
          <h1
            className="text-white text-3xl font-black uppercase tracking-wider"
            style={{ textShadow: generateTextShadow(1) }}
          >
            1v1
          </h1>
          {dateLine && (
            <span
              className="text-white text-sm font-bold uppercase"
              style={{ textShadow: generateTextShadow(0.5) }}
            >
              {dateLine}
            </span>
          )}
          <div className="flex items-center gap-1.5 mt-0.5">
            <span
              className="text-white text-sm font-bold uppercase"
              style={{ textShadow: generateTextShadow(0.5) }}
            >
              Entry fee:
            </span>
            <img
              src={REWARD_ICONS[0]}
              alt="USDC"
              className="w-4 h-4"
              style={{ imageRendering: "pixelated" }}
            />
            <span
              className="text-white text-sm font-black"
              style={{ textShadow: generateTextShadow(0.5) }}
            >
              {view.entryUsdc}
            </span>
          </div>
        </div>

        {view.players.length > 0 && (
          <div className="flex flex-col items-center gap-3 px-4 pb-6">
            <PlayersRow players={view.players} />
          </div>
        )}
      </div>

      {/* Bottom: cancelled control panel */}
      <MatchCancelledControl
        refundAmount={view.entryUsdc}
        refundsTx={refundsTx}
        onBackToLobby={() => navigate({ to: "/home" })}
        className="h-full max-h-[40svh]"
      />
    </>
  );
}

function MatchCancelledControl({
  refundAmount,
  refundsTx,
  onBackToLobby,
  className,
}: {
  refundAmount: number;
  refundsTx: string | null;
  onBackToLobby: () => void;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex-1 flex flex-col px-2 pt-2 bg-[#1D2A52] border-t-4 border-[#0659B5] overflow-visible",
        className,
      )}
    >
      <div className="flex-1 flex flex-col items-center justify-between py-6">
        <div className="flex flex-col items-center justify-center">
          <PrizeWrapper
            prizes={[
              {
                icon: REWARD_ICONS[0],
                value: refundAmount,
                alt: "USDC",
                isPositive: true,
              },
            ]}
            iconSize="h-8"
            textSize="text-base"
            className="py-0"
          />
          <p className="text-sm font-bold text-white/80 mt-1 text-center">
            Refunded
          </p>
        </div>
        <div className="flex flex-col items-center justify-center">
          <h2 className="text-3xl font-bold text-white uppercase">
            Match cancelled
          </h2>
          <p className="text-base font-bold text-white/80 text-center">
            Your entry has been refunded
          </p>
        </div>

        <div className="flex flex-col items-center justify-center">
          <DefaultButton
            variant="default"
            className="w-fit h-10 uppercase text-sm mt-6"
            onClick={onBackToLobby}
          >
            Back to lobby
          </DefaultButton>
          {refundsTx && (
            <a
              href={solscanTxUrl(refundsTx)}
              target="_blank"
              rel="noreferrer"
              className="text-xs font-bold text-white/80 mt-1 hover:cursor-pointer hover:text-white hover:underline active:scale-95 transition-all duration-300"
            >
              View distribution
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

// ============ Shared bits (kept compact) ============

function PlayersRow({ players }: { players: MatchmakingPlayer[] }) {
  if (players.length === 0) return null;
  const [host, challenger] = players;
  return (
    <div className="flex items-stretch gap-3 w-full max-w-md">
      <PlayerCard player={host} />
      <div className="flex items-center justify-center px-2">
        <span
          className="text-white text-xl font-black"
          style={{ textShadow: generateTextShadow(0.5) }}
        >
          VS
        </span>
      </div>
      <PlayerCard player={challenger} />
    </div>
  );
}

function PlayerCard({ player }: { player: MatchmakingPlayer | undefined }) {
  if (!player) {
    return <div className="flex-1 dark-green-panel min-h-[120px]" />;
  }
  return (
    <div className="flex-1 dark-green-panel p-3 flex flex-col items-center gap-1">
      <img
        src={player.ownerProfile.profilePicture}
        alt={player.ownerProfile.username}
        className="w-12 h-12 rounded-full object-cover"
      />
      <span
        className="text-white text-xs font-black truncate w-full text-center"
        style={{ textShadow: generateTextShadow(0.3) }}
      >
        {player.ownerProfile.username}
      </span>
    </div>
  );
}

function CenteredMessage({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-3 px-4 home-wallpaper-background">
      <span
        className="text-white text-xl font-black uppercase tracking-wider"
        style={{ textShadow: generateTextShadow(0.7) }}
      >
        {title}
      </span>
      {description && (
        <span
          className="text-white/80 text-sm text-center max-w-md"
          style={{ textShadow: generateTextShadow(0.3) }}
        >
          {description}
        </span>
      )}
      {action}
    </div>
  );
}

function solscanTxUrl(signature: string): string {
  const base = `https://solscan.io/tx/${signature}`;
  return configs.IS_TESTNET ? `${base}?cluster=devnet` : base;
}

function extractError(error: unknown): string {
  if (typeof error === "object" && error !== null) {
    if ("response" in error) {
      const e = error as { response?: { data?: { error?: string } } };
      if (e.response?.data?.error) return e.response.data.error;
    }
    if ("message" in error) return (error as Error).message;
  }
  return "Something went wrong.";
}
